import {
    Avatar,
    Button,
    Card,
    CardActions,
    CardContent,
    CardHeader,
    Checkbox,
    colors,
    Divider,
    Link,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TablePagination,
    TableRow,
    Theme,
    Typography
} from '@material-ui/core';
import {EmailOutlined} from '@material-ui/icons';
import {makeStyles} from '@material-ui/styles';
import clsx from 'clsx';
import React, {useState} from 'react';
import PerfectScrollbar from 'react-perfect-scrollbar';
import {Link as RouterLink} from 'react-router-dom';
import {Can} from '../../../components/Can';
import Label from '../../../components/Label';
import {TableEditBar} from '../../../components/TableEditBar';
import {Role, User} from '../../../graphql/auth';
import {PaginatiorInfo} from '../../../graphql/common.datatypes';
import getInitials from '../../../utils/getInitials';
import UsersSendMessage from '../UsersSendMessageModal';
import useRouter from 'use-react-router';

const avatar = require('../../../../assets/img/avatar.svg');

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        position: 'relative',
    },
    content: {
        padding: 0
    },
    inner: {
      [theme.breakpoints.up('md')]: {
        minWidth: 700,
      },
    },
    nameCell: {
        display: 'flex',
        alignItems: 'center'
    },
    avatar: {
        height: 42,
        width: 42,
        marginRight: theme.spacing(1)
    },
    actions: {
        padding: theme.spacing(1),
        justifyContent: 'flex-end'
    },
    buttonIcon: {
        marginRight: theme.spacing(1)
    }
}));

const Results = (props: {
    className?: string,
    users: User[],
    paginatorInfo: PaginatiorInfo,
    setPage: (page: number) => void,
    rowsPerPage: number,
    setRowsPerPage: (rowsPerPage: number) => void
}) => {
    const {className, rowsPerPage, setPage, setRowsPerPage, paginatorInfo, users, ...rest} = props;
    const classes = useStyles();
    const [openMessage, setOpenMessage] = useState(false);
    const [selectedUsers, setSelectedUsers] = useState(new Array<number>());
    const {history} = useRouter();

    const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newSelectedUsers = event.target.checked
            ? users.map((customer: User) => customer.id)
            : [];

        setSelectedUsers(newSelectedUsers);
    };

    const handleSelectOne = (event: React.ChangeEvent<HTMLInputElement>, id: number) => {
        const selectedIndex = selectedUsers.indexOf(id);
        let newSelectedUsers = new Array<number>();

        if (selectedIndex === -1) {
            newSelectedUsers = newSelectedUsers.concat(selectedUsers, id);
        } else if (selectedIndex === 0) {
            newSelectedUsers = newSelectedUsers.concat(
                selectedUsers.slice(1)
            );
        } else if (selectedIndex === selectedUsers.length - 1) {
            newSelectedUsers = newSelectedUsers.concat(
                selectedUsers.slice(0, -1)
            );
        } else if (selectedIndex > 0) {
            newSelectedUsers = newSelectedUsers.concat(
                selectedUsers.slice(0, selectedIndex),
                selectedUsers.slice(selectedIndex + 1)
            );
        }

        setSelectedUsers(newSelectedUsers);
    };

    const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, page: number) => {
        setPage(page + 1);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        setRowsPerPage(Number(event.target.value));
    };

    const showSendMessage = () => {
        setOpenMessage(true);
    };

    const handleMessageClose = () => {
        setOpenMessage(false);
    };

    return (
        <div {...rest} className={clsx(classes.root, className)}>
            <Typography color="textSecondary" gutterBottom variant="body2">
                Page {paginatorInfo.currentPage} of{' '}{paginatorInfo.lastPage}
            </Typography>
            <Card>
                <CardHeader title="All users"/>
                <Divider/>
                <CardContent className={classes.content}>
                    <PerfectScrollbar>
                        <div className={classes.inner}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                checked={selectedUsers.length === users.length}
                                                color="primary"
                                                indeterminate={
                                                    selectedUsers.length > 0 &&
                                                    selectedUsers.length < users.length
                                                }
                                                onChange={handleSelectAll}
                                            />
                                        </TableCell>
                                        <TableCell>Name</TableCell>
                                        <TableCell>Email</TableCell>
                                        <TableCell>Roles</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {users.map(user => (
                                        <TableRow
                                            hover
                                            key={user.id}
                                            selected={selectedUsers.indexOf(user.id) !== -1}
                                        >
                                            <TableCell padding="checkbox">
                                                <Checkbox
                                                    checked={
                                                        selectedUsers.indexOf(user.id) !== -1
                                                    }
                                                    color="primary"
                                                    onChange={(event) => {
                                                        event.stopPropagation();
                                                        event.preventDefault();
                                                        handleSelectOne(event, user.id);
                                                    }}
                                                    value={selectedUsers.indexOf(user.id) !== -1}
                                                />
                                            </TableCell>
                                            <TableCell
                                                onClick={() => history.push('/users/' + user.id)}
                                            >
                                                <div className={classes.nameCell}>
                                                    <Avatar
                                                        className={classes.avatar}
                                                        src={avatar}
                                                    >
                                                        {getInitials(user.first_name + ' ' + user.last_name)}
                                                    </Avatar>
                                                    <div>
                                                        <Link
                                                            color="inherit"
                                                            component={RouterLink}
                                                            to={`/users/${user.id}`}
                                                            variant="h6"
                                                        >
                                                            {user.last_name}, {user.first_name}
                                                        </Link>
                                                    </div>
                                                </div>
                                            </TableCell>
                                            <TableCell
                                                onClick={() => history.push('/users/' + user.id)}
                                            >{user.email}</TableCell>
                                            <TableCell
                                                onClick={() => history.push('/users/' + user.id)}
                                            >{user.roles!.map((role: Role, i) => (
                                                <Label key={i} variant="outlined"
                                                       color={colors.green[600]}>{role.name}</Label>))}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    </PerfectScrollbar>
                </CardContent>
                <CardActions className={classes.actions}>
                    <TablePagination
                        component="div"
                        count={paginatorInfo.total}
                        onChangePage={handleChangePage}
                        onChangeRowsPerPage={handleChangeRowsPerPage}
                        page={paginatorInfo.currentPage - 1}
                        rowsPerPage={rowsPerPage}
                        rowsPerPageOptions={[5, 10, 25]}
                    />
                </CardActions>
            </Card>
            <TableEditBar selected={selectedUsers}>
                <Can permission="user.message">
                    <Button onClick={showSendMessage}>
                        <EmailOutlined className={classes.buttonIcon}/>
                        Message Users
                    </Button>
                </Can>
            </TableEditBar>

            <UsersSendMessage open={openMessage} userIds={selectedUsers} onClose={handleMessageClose}/>
        </div>
    );
};

export default Results;
